import {
  Box,
  Button,
  CheckboxInput,
  FormControl,
  FormGroup,
  Input,
} from '@deckee/deck-hand';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import PhoneInput from 'react-phone-number-input';
import styled from 'styled-components';
import * as yup from 'yup';
import CheckListItem from '../CheckListItem/CheckListItem';
import Form from '../Form/Form';

const StyledPhoneInput = styled(PhoneInput)`
  & input {
    clear: both;
    text-align: inherit;
    align-items: center;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    box-shadow: none;
    box-sizing: border-box;
    color: #363636;
    display: inline-flex;
    font-family: Helvetica Neue, sans-serif;
    height: 100%;
    -webkit-box-pack: start;
    justify-content: flex-start;
    padding: 0.5em 0.75em;
    position: relative;
    transition: all 0.3s;
    vertical-align: top;
    border-radius: 8px;
    font-size: 16px;
    max-width: 150px;
  }
`;

const ContactDetailsFormSchema = yup.object({
  contactNumber: yup.string().required('Contact number must be provided.'),
  contactName: yup.string().trim().required('Contact name must be provided.'),
  contactEmail: yup
    .string()
    .email('Contact email must be a valid email address.')
    .required('Contact email must be provided.'),
  confirmation: yup
    .boolean()
    .isTrue('Your consent is required to store this incident report.'),
});

const ContactDetailsForm = (props) => {
  const {
    onSubmit,
    contactEmail: defaultEmail,
    contactNumber: defaultNumber,
    contactName: defaultName,
  } = props;

  const initialValues = {
    contactNumber: defaultNumber,
    contactName: defaultName,
    contactEmail: defaultEmail,
    confirmation: false,
  };

  const handleSubmit = (formValues) => {
    // using the yup schema cast to trim form values where needed
    onSubmit(ContactDetailsFormSchema.cast(formValues));
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ContactDetailsFormSchema}
      onSubmit={handleSubmit}
    >
      {({
        handleSubmit: formikHandleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue,
      }) => (
        <Form onSubmit={formikHandleSubmit}>
          <FormGroup heading="How can we contact you?">
            <FormControl
              label="Number"
              name="contactNumber"
              error={touched.contactNumber && errors.contactNumber}
              required
            >
              <StyledPhoneInput
                name="contactNumber"
                onChange={(value) => {
                  const newValue = value ?? '';
                  setFieldValue('contactNumber', newValue, true);
                }}
                onBlur={handleBlur}
                value={values.contactNumber}
                defaultCountry="AU"
                initialValueFormat="national"
                displayInitialValueAsLocalNumber
                limitMaxLength
              />
            </FormControl>

            <FormControl
              label="Name"
              name="contactName"
              error={touched.contactName && errors.contactName}
              required
            >
              <Input
                value={values.contactName}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormControl>
            <FormControl
              label="Email"
              name="contactEmail"
              error={touched.contactEmail && errors.contactEmail}
              required
            >
              <Input
                value={values.contactEmail}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormControl>
            <FormControl
              name="confirmation"
              error={touched.confirmation && errors.confirmation}
              required
            >
              <CheckListItem py={2}>
                <CheckboxInput
                  checked={values.confirmation}
                  name="confirmation"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <Box
                  as="label"
                  flex="1 1 auto"
                  pl="3"
                  fontSize="1"
                  htmlFor="confirmation"
                >
                  I accept that the information provided may be shared with
                  relevant agencies and I am willing to appear in court as a
                  witness if required.
                </Box>
              </CheckListItem>
            </FormControl>
          </FormGroup>
          <FormGroup mt="auto">
            <Button.Group my={3} wide width="100%">
              <Button type="submit">Submit</Button>
            </Button.Group>
          </FormGroup>
        </Form>
      )}
    </Formik>
  );
};

ContactDetailsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,

  contactEmail: PropTypes.string,
  contactNumber: PropTypes.string,
  contactName: PropTypes.string,
};

ContactDetailsForm.defaultProps = {
  contactEmail: '',
  contactNumber: '',
  contactName: '',
};

export default ContactDetailsForm;
